import React from 'react';
import { Search, X, Upload, Users, Filter, Calendar, Sparkles } from 'lucide-react';
import type { Theme } from '../themes/theme';

export interface DriveToolbarProps {
  theme: Theme;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  bulkActionMessage: string | null;
  selectedCount: number;
  isBulkActionLoading: boolean;
  onClearSelection: () => void;
  onFileSelect?: () => void;
  onFileSelectShared?: () => void;
  isUploading?: boolean;
  isAdmin?: boolean;
  filterDropdownRef?: React.RefObject<HTMLButtonElement | null>;
  onToggleFilterDropdown?: () => void;
  periodDropdownRef?: React.RefObject<HTMLButtonElement | null>;
  onTogglePeriodDropdown?: () => void;
  searchInContent?: boolean;
  toggleSearchInContent?: () => void;
}

const DriveToolbar: React.FC<DriveToolbarProps> = ({
  theme,
  searchTerm,
  onSearchChange,
  onClearSearch,
  bulkActionMessage,
  selectedCount,
  isBulkActionLoading,
  onClearSelection,
  onFileSelect,
  onFileSelectShared,
  isUploading = false,
  isAdmin = false,
  filterDropdownRef,
  onToggleFilterDropdown,
  periodDropdownRef,
  onTogglePeriodDropdown,
  searchInContent = false,
  toggleSearchInContent,
}) => (
  <div
    className={`${theme.colors.bg.input} border ${theme.colors.border.primary} rounded-2xl shadow-sm relative`}
    style={{ overflow: 'visible', outline: 'none' }}
  >
    {/* Input de búsqueda - Parte superior */}
    <div className="px-4 py-3">
      <div className="relative flex items-center gap-2">
        <Search className={`w-4 h-4 ${theme.colors.text.secondary} flex-shrink-0`} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar en Drive..."
          className={`flex-1 bg-transparent border-none outline-none text-sm ${theme.colors.text.primary} placeholder-gray-400 focus:ring-0 focus:outline-none pr-10`}
          style={{ boxShadow: 'none' }}
        />
        <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {searchTerm && (
            <button
              type="button"
              onClick={onClearSearch}
              className={`p-2 rounded-lg ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
              title="Limpiar búsqueda"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Separador visual */}
    <div className={`border-t ${theme.colors.border.primary}`} />

    {/* Botones de acción - Parte inferior */}
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-3`}>
      {/* Botones de Filtros a la izquierda */}
      <div className="flex items-center gap-2 flex-wrap">
        {onToggleFilterDropdown && filterDropdownRef && (
          <button
            ref={filterDropdownRef as React.LegacyRef<HTMLButtonElement>}
            type="button"
            onClick={onToggleFilterDropdown}
            className={`p-2 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
            title="Filtrar documentos"
          >
            <Filter className="w-4 h-4" />
          </button>
        )}
        {onTogglePeriodDropdown && periodDropdownRef && (
          <button
            ref={periodDropdownRef as React.LegacyRef<HTMLButtonElement>}
            type="button"
            onClick={onTogglePeriodDropdown}
            className={`p-2 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
            title="Filtrar por período"
          >
            <Calendar className="w-4 h-4" />
          </button>
        )}
        {toggleSearchInContent && (
          <button
            type="button"
            onClick={toggleSearchInContent}
            className={`p-2 rounded-lg border ${theme.colors.border.primary} transition-colors ${
              searchInContent
                ? `${theme.colors.bg.tertiary} ${theme.colors.text.primary}`
                : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary}`
            }`}
            title={
              searchInContent
                ? 'Buscando en contenido (click para desactivar)'
                : 'Buscar solo en nombres (click para buscar en contenido)'
            }
          >
            <Sparkles className={`w-4 h-4 ${searchInContent ? 'text-nb-accent' : ''}`} />
          </button>
        )}
      </div>

      {/* Información de selección y botones de Upload agrupados a la derecha */}
      <div className="flex items-center gap-2 flex-wrap">
        {bulkActionMessage && (
          <span className={`text-xs font-medium ${theme.colors.text.secondary}`}>
            {bulkActionMessage}
          </span>
        )}
        {selectedCount > 0 && (
          <>
            <span className={`text-xs font-medium ${theme.colors.text.secondary}`}>
              {selectedCount} seleccionado{selectedCount !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className={`p-1 rounded-lg ${theme.colors.text.secondary} hover:${
                theme.colors.text.primary
              } hover:${theme.colors.bg.secondary} transition-colors ${
                isBulkActionLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Limpiar selección"
              disabled={isBulkActionLoading}
            >
              <X className="w-3 h-3" />
            </button>
          </>
        )}
        {(selectedCount > 0 || bulkActionMessage) &&
          (onFileSelect || (isAdmin && onFileSelectShared)) && (
            <div className={`w-[1px] h-6 bg-nb-border/40`} />
          )}
        <div className="flex items-center gap-2">
          {onFileSelect && (
            <button
              onClick={onFileSelect}
              disabled={isUploading}
              className={`p-2 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors disabled:opacity-50`}
              title="Subir documentos privados"
            >
              <Upload className="w-4 h-4" />
            </button>
          )}
          {isAdmin && onFileSelectShared && (
            <button
              onClick={onFileSelectShared}
              disabled={isUploading}
              className={`p-2 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors disabled:opacity-50`}
              title="Subir documentos a la biblioteca compartida (solo admin)"
            >
              <Users className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default DriveToolbar;
